import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import DataTable from "shared/ui/Table/DataTable/DataTable.component"
import type { DataTableProps } from "shared/ui/Table/DataTable/DataTable.component"
import { ptToPx } from "shared/ui/Table/style/resolveCellStyle"

/** The smallest body text, in px, a table shrinks to before it scrolls sideways. */
const MIN_FONT_PX = 10
/** Fit changes smaller than half of this are dropped, so measuring settles. */
const FIT_STEP = 0.01
/** A fit aims this far inside the room: the cell borders do not scale. */
const FIT_MARGIN = 0.98

/** What an overflow measured: the width the table needs at fit 1. */
interface Need {
  model: DataTableProps["model"]
  style: DataTableProps["style"]
  base: number
  /** The box's outer width then (scrollbar included). */
  outer: number
  width: number
}

export interface TableFitProps extends Omit<DataTableProps, "layout"> {
  /** Shown in a small bar above the table; no bar when undefined. */
  toolbar?: React.ReactNode
}

/**
 * A DataTable that fits its box, with no zoom and no panning. The table
 * spans the box's width. When its one-line cells need more room than the box
 * has, its font (and cell padding) shrinks in proportion, never below
 * MIN_FONT_PX body text; a table still wider at that size scrolls sideways,
 * and a table taller than the box scrolls down. `fontScale` is the chosen
 * size: the fit starts from it and grows back to it when the box widens. The
 * fit is measured after every render and whenever the box or the table
 * changes size (the grid resizing the tile).
 */
const TableFit: React.FC<TableFitProps> = ({ toolbar, fontScale, ...table }) => {
  const boxRef = useRef<HTMLDivElement>(null)
  const refitRef = useRef<() => void>(() => undefined)
  const needRef = useRef<Need | null>(null)
  const [fit, setFit] = useState(1)
  const { model, style } = table
  const base = fontScale && fontScale > 0 ? fontScale : 1
  // A chosen size already under the minimum never shrinks.
  const floor = Math.min(1, MIN_FONT_PX / (ptToPx(style.font.sizePt) * base))

  const refit = (): void => {
    const box = boxRef.current
    if (!box || box.clientWidth <= 0) return
    const room = box.clientWidth
    const used = box.scrollWidth
    let next: number
    if (used > room) {
      // Too wide: shrink in proportion (at least one step), down to the floor.
      needRef.current = {
        model,
        style,
        base,
        outer: box.offsetWidth,
        width: used / fit,
      }
      next = Math.min(fit - FIT_STEP, (fit * room * FIT_MARGIN) / used)
    } else if (fit < 1) {
      const need = needRef.current
      const same =
        need !== null &&
        need.model === model &&
        need.style === style &&
        need.base === base
      if (!same) {
        // A new table, look or size: start from the chosen size.
        next = 1
      } else if (box.offsetWidth > need.outer) {
        // The box got wider since the table last overflowed: grow into it.
        // Its outer width ignores a vertical scrollbar coming and going.
        next = (room * FIT_MARGIN) / need.width
        if (next <= fit) return
      } else {
        return
      }
    } else {
      return
    }
    next = Math.round(Math.min(1, Math.max(floor, next)) * 1000) / 1000
    if (Math.abs(next - fit) > FIT_STEP / 2) setFit(next)
  }

  // Measured before paint, so a new table never shows at the wrong size.
  useLayoutEffect(() => {
    refitRef.current = refit
    refit()
  })

  useEffect(() => {
    const box = boxRef.current
    if (!box || typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(() => refitRef.current())
    observer.observe(box)
    if (box.firstElementChild) observer.observe(box.firstElementChild)
    return () => observer.disconnect()
  }, [])

  return (
    <div className="dq-table-fit">
      {toolbar !== undefined && (
        <div className="dq-table-fit__bar">{toolbar}</div>
      )}
      <div ref={boxRef} className="dq-table-fit__scroll" data-testid="table-fit">
        <DataTable {...table} layout="fill" fontScale={base * fit} />
      </div>
    </div>
  )
}

export default TableFit
